<script setup lang="ts">
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";
import { useI18n } from 'vue-i18n';
import { ref } from "vue";

const { open } = defineProps({
  open: {
    type: Boolean,
    required: true
  }
});

defineEmits(['on-close']);

const { t } = useI18n();

const passportPhoto = ref<File | null>(null);
const sailingLicensePhoto = ref<File | null>(null);
const hasSailingLicense = ref(false);

const handleFileUpload = (event: Event, type: "passport" | "sailing") => {
  const file = (event.target as HTMLInputElement).files?.[0] || null;
  if (type === "passport") {
    passportPhoto.value = file;
  } else {
    sailingLicensePhoto.value = file;
  }
};
</script>

<template>
  <CustomModal :show="open" @on-close="$emit('on-close')">
    <div class="h-[745px] w-[831px] overflow-x-auto">
      <div class="flex flex-col p-5">
        <div class="cursor-pointer self-end" type="default" @click="$emit('on-close')">
          <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
        </div>
        <div class="px-14 pt-3.5 pb-14">
          <div class="responsive-title-6 font-semibold">{{ t('modal.addCrewMember') }}</div>
          <div class="responsive-body-xsmall bg-notifications-pending py-2.5 px-5 mt-11 rounded-8xl">
            {{ t('modal.warning') }}
          </div>
          <div class="mt-8 grid grid-cols-2 gap-x-8 gap-y-6">
            <VTextField :label="t('modal.firstName')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.lastName')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.nationality')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.dob')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.passportNumber')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.passportExpiryDate')" variant="underlined"></VTextField>
            <div class="col-span-2 flex flex-row gap-2 items-end">
              <div>
                <div class="responsive-body-xsmall ">{{ t('modal.passportPhoto') }}</div>
                <div class="responsive-body-xsmall text-black/40">{{ t('modal.fileTypes') }}</div>
              </div>
              <div class="relative">
                <CustomButton :outlined="true" :rounded="true" class="h-9 px-4 border border-black text-black hover:bg-black hover:text-white transition duration-200">
                  {{ t('modal.browseFile') }}
                  <input type="file" class="absolute inset-0 opacity-0 cursor-pointer" 
                         @change="(event) => handleFileUpload(event, 'passport')" />
                </CustomButton>
              </div>
              <div class="responsive-body-xsmall text-black/40">
                {{ passportPhoto ? passportPhoto.name : t('modal.noFileChosen') }}
              </div>
            </div>
            <VTextField :label="t('modal.email')" variant="underlined"></VTextField>
            <VTextField :label="t('modal.phoneNumber')" variant="underlined"></VTextField>
            <div class="col-span-2 flex items-center gap-3">
              <span class="responsive-body-medium">{{ t('modal.sailingLicense') }}</span>
              <VSwitch inset v-model="hasSailingLicense" base-color="grey" color="black" />
            </div>
            <div v-if="hasSailingLicense" class="col-span-2 flex flex-row gap-6 items-end">
              <div>
                <div class="responsive-body-xsmall">{{ t('modal.sailingLicensePhoto') }}</div>
                <div class="responsive-body-xsmall text-black/40">{{ t('modal.fileTypes') }}</div>
              </div>
              <div class="relative flex flex-row align-center">
                <CustomButton :outlined="true" :rounded="true" class=" h-9 px-4 border border-black text-black hover:bg-black hover:text-white transition duration-200">
                  {{ t('modal.browseFile') }}
                  <input type="file" class="absolute inset-0 opacity-0 cursor-pointer" 
                         @change="(event) => handleFileUpload(event, 'sailing')" />
                </CustomButton>
                <div class="responsive-body-xsmall text-black/40">
                  {{ sailingLicensePhoto ? sailingLicensePhoto.name : t('modal.noFileChosen') }}
                </div>
              </div>
            </div>
            <CustomButton type="black" :outlined="false" :rounded="true" class="w-[92px]">{{ t('modal.addButton') }}</CustomButton>
          </div>
        </div>
      </div>
    </div>
  </CustomModal>
</template>

<style scoped></style>
